import { ConvexError } from "convex/values";

export const CATEGORY_LABELS = {
  cheat: "Cheat",
  team_up: "Team up",
  insultes_admins: "Insultes admins",
} as const;

export const STATUS_LABELS: Record<string, string> = {
  active: "Actif", lifted: "Levé", pending: "À valider", confirmed: "Confirmé", visitor: "Visiteur",
  rejected: "Écarté", queued: "En attente", ingesting: "Import", running: "Analyse",
  completed: "Terminé", failed: "Échec", cancelled: "Annulé", admin: "Administrateur", viewer: "Lecture",
  disabled: "Désactivé", revoked: "Révoqué", stopped: "Arrêté", expired: "Expiré", handled: "Traité", dismissed: "Écarté",
};

export const ROLE_LABELS: Record<string, string> = { admin: "Administrateur", viewer: "Lecture seule", visitor: "Visiteur" };

export const ACTOR_LABELS: Record<string, string> = { user: "Utilisateur", api_key: "Clé API", system: "Système" };

export const ACTION_LABELS: Record<string, string> = {
  "blacklist.create": "Ajout d’une entrée",
  "blacklist.update": "Modification d’une entrée",
  "blacklist.active": "Réactivation d’une sanction",
  "blacklist.lifted": "Levée d’une sanction",
  "blacklist.delete": "Suppression d’une entrée",
  "blacklist.redated": "Dates de sanction corrigées",
  "suspect.confirmed": "Suspect confirmé",
  "suspect.rejected": "Suspect écarté",
  "suspect.deleted": "Signalement supprimé",
  "suspect.purged": "Journal des signalements vidé",
  "suspect.acknowledged": "Signalement marqué comme vu",
  "suspect.acknowledged_all": "Signalements marqués comme vus",
  "check.completed": "Contrôle terminé",
  "check.suspects_restored": "Signalements du contrôle restaurés",
  "check.failed": "Contrôle en échec",
  "check.cancelled": "Contrôle annulé",
  "check.deleted": "Contrôle supprimé",
  "watch.start": "Surveillance publique démarrée",
  "watch.private_start": "Surveillance privée démarrée",
  "watch.stop": "Surveillance arrêtée",
  "watch.rename": "Surveillance renommée",
  "watch.reschedule": "Horaires de surveillance modifiés",
  "watch.alert_blacklisted": "Alerte ajoutée à la blacklist",
  "watch.alert_handled": "Alerte marquée comme traitée",
  "watch.alert_dismissed": "Alerte écartée",
  "user.create": "Création d’un compte",
  "user.set_discord_id": "Modification de l’ID Discord",
  "user.set_role": "Changement de rôle",
  "user.disable": "Compte désactivé",
  "user.enable": "Compte réactivé",
  "user.reset_password": "Mot de passe réinitialisé",
  "user.change_password": "Mot de passe personnel modifié",
  "user.delete": "Suppression d’un compte",
  "user.bootstrap_admin": "Premier administrateur",
  "api_key.create": "Création d’une clé API",
  "api_key.reveal": "Consultation d’une clé API",
  "api_key.revoke": "Révocation d’une clé API",
  "api.check": "Vérification via l’API",
  "api.blacklist_list": "Export via l’API",
  "migration.completed": "Migration réussie",
  "migration.failed": "Migration en échec",
  "settings.update": "Réglage modifié",
  "settings.wls_token_update": "Token WLS mis à jour",
  "settings.discord_bot_update": "Bot Discord configuré",
  "settings.alert_dm_update": "Destinataires des DM d’alerte modifiés",
  "watch.dm_recipients": "Destinataires des DM modifiés",
};

export function actionLabel(action: string) {
  return ACTION_LABELS[action] ?? action.replaceAll(".", " · ").replaceAll("_", " ");
}

/** Libellé du signal de rapprochement, tel qu'il doit apparaître au relecteur. */
export const SIGNAL_LABELS: Record<string, string> = {
  epic_id_exact: "Epic ID identique",
  discord_id_exact: "Discord ID identique",
  discord_changed_for_epic: "Nouveau Discord sur un Epic banni",
  epic_changed_for_discord: "Nouveau compte Epic sur un Discord banni",
  epic_pseudo_exact: "Pseudo Epic identique",
  discord_pseudo_exact: "Pseudo Discord identique",
  epic_pseudo_skeleton: "Pseudo Epic maquillé",
  discord_pseudo_skeleton: "Pseudo Discord maquillé",
  wls_name_skeleton: "Pseudo WLS proche",
  pseudo_distance_1: "Pseudo à 1 caractère près",
  pseudo_distance_2: "Pseudo à 2 caractères près",
};

export function signalLabel(signal: string) {
  return SIGNAL_LABELS[signal] ?? signal.replaceAll("_", " ");
}

/** Signaux qui établissent qu'un joueur banni est revenu sous un autre compte. */
export const EVASION_SIGNALS = new Set(["discord_changed_for_epic", "epic_changed_for_discord"]);

export function formatDate(value?: number) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

/**
 * Date compacte pour les tableaux : « 14/08/2026 19:34 » tient sur bien moins de place que la
 * forme longue, sur une colonne qui n'est jamais la raison pour laquelle on ouvre la page.
 */
export function formatDateCompact(value?: number) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(new Date(value)).replace(", ", " ");
}

/** Date seule, pour les colonnes serrées : l'heure reste accessible au survol. */
export function formatDateShort(value?: number) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }).format(new Date(value));
}

/**
 * Mois et année seulement — la forme retenue pour les dates de sanction. Le jour des entrées
 * migrées est celui de l'import, pas du bannissement : l'afficher laisserait croire à une
 * précision qui n'existe pas. La date complète reste accessible au survol.
 */
export function formatMonthYear(value?: number) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("fr-FR", { month: "2-digit", year: "numeric" }).format(new Date(value));
}

export function formatNumber(value: number) {
  return new Intl.NumberFormat("fr-FR").format(value);
}

/**
 * Les erreurs applicatives Convex portent leur message lisible dans `data` ; `message` contient
 * la trace serveur (chemin de fichier et ligne), qui ne doit jamais atteindre l'utilisateur.
 */
export function errorMessage(error: unknown) {
  if (error instanceof ConvexError) {
    const data = (error as ConvexError<string | { message?: string }>).data;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data === "object" && typeof data.message === "string") return data.message;
    return "L’opération a été refusée.";
  }
  return "Une erreur inattendue est survenue. Réessayez dans un instant.";
}
